import type React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevDownIcon } from '../icons'
import type { IconProps } from '../icons/types'
import { cn, cva } from '../lib/cva'
import { type BaseFormElementProps, FormElementWrapper, formStyles, useFormElementState } from './FormElementWrapper'

interface SelectOption {
	value: string
	label: string
	icon?: React.FC<IconProps>
}

interface SelectProps extends BaseFormElementProps {
	placeholder?: string
	options: SelectOption[]
	value?: string | string[]
	displaySelectedValue?: boolean
	sizing?: 'small' | 'regular'
	selectIcon?: React.FC<IconProps> | null
	defaultValue?: string | string[]
	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- needed for overloading
	onChange?: (value: any) => void
	isMulti?: boolean
	onMenuScrollToBottom?: () => void
	isLoadingMore?: boolean
	hasMore?: boolean
	searchable?: boolean
	onSearch?: (query: string) => void
}
interface SingleSelectProps extends SelectProps {
	onChange?: (value: string) => void
	isMulti?: false
}
interface MultiSelectProps extends SelectProps {
	onChange?: (value: string[]) => void
	isMulti: true
}

const selectStyles = {
	dropdownMenu: cva(
		'absolute z-150 w-full bg-white border border-neutral-300 rounded-sm shadow-lg max-h-50 overflow-y-scroll',
		{
			variants: {
				isOpen: {
					true: 'block',
					false: 'hidden',
				},
				sizing: {
					small: 'text-sm',
					regular: '',
				},
			},
			defaultVariants: {
				isOpen: false,
				sizing: 'regular',
			},
		},
	),

	option: cva('px-4 py-2 hover:bg-neutral-100 cursor-pointer flex items-center', {
		variants: {
			isSelected: {
				true: 'bg-primary-50',
				false: '',
			},
			hasIcon: {
				true: 'px-1',
				false: '',
			},
		},
		defaultVariants: {
			isSelected: false,
			hasIcon: false,
		},
	}),
}

const COMBOBOX_TOGGLE_KEYS = new Set(['Enter', ' '])

function handleComboboxKeyDown(event: React.KeyboardEvent, toggleDropdown: () => void) {
	if (COMBOBOX_TOGGLE_KEYS.has(event.key)) toggleDropdown()
}

export function Select(props: SelectProps): React.ReactElement
export function Select(props: SingleSelectProps): React.ReactElement
export function Select(props: MultiSelectProps): React.ReactElement
export function Select({
	label,
	hint,
	showHintIcon = false,
	variant = 'default',
	disabled = false,
	className,
	options,
	value,
	defaultValue,
	onChange,
	placeholder = 'Select',
	isRequired = false,
	isMulti = false,
	onMenuScrollToBottom,
	isLoadingMore = false,
	hasMore = false,
	displaySelectedValue = true,
	selectIcon: SelectIcon = null,
	sizing = 'regular',
	searchable = false,
	onSearch,
}: SelectProps): React.ReactElement {
	const [isOpen, setIsOpen] = useState(false)
	const [searchQuery, setSearchQuery] = useState('')
	const [focusedIndex, setFocusedIndex] = useState(-1)
	const selectRef = useRef<HTMLDivElement>(null)
	const dropdownRef = useRef<HTMLDivElement>(null)
	const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const selectedLabelsCache = useRef<Map<string, string>>(new Map())

	const {
		value: selectedValue,
		setInternalValue,
		styleProps,
	} = useFormElementState({
		value: isMulti ? undefined : value,
		defaultValue: isMulti ? undefined : defaultValue,
		variant,
		disabled,
	})

	const [multiValue, setMultiValue] = useState<string[]>(isMulti ? (Array.isArray(value) ? value : []) : [])

	const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })
	const [containerWidth, setContainerWidth] = useState(0)
	const [openUpward, setOpenUpward] = useState(false)
	const updatedRef = useRef(false)

	const updateDropdownPosition = useCallback(() => {
		if (selectRef.current && !updatedRef.current) {
			const rect = selectRef.current.getBoundingClientRect()
			setContainerWidth(rect.width)

			// max-h-50 = 200px (12.5rem * 16)
			const dropdownMaxHeight = 200
			const spaceBelow = window.innerHeight - rect.bottom
			const spaceAbove = rect.top

			// Flip to open upward if not enough space below but enough above
			const shouldOpenUpward = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow
			setOpenUpward(shouldOpenUpward)

			setDropdownPosition({
				top: shouldOpenUpward ? rect.top : rect.bottom,
				left: rect.left,
				width: rect.width,
			})
			updatedRef.current = true
		}
	}, [])

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const dropdown = document.querySelector('[data-select-dropdown]')
			if (
				selectRef.current &&
				!selectRef.current.contains(event.target as Node) &&
				dropdown &&
				!dropdown.contains(event.target as Node)
			) {
				if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
				setIsOpen(false)
				setSearchQuery('')
				onSearch?.('')
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
			if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
		}
	}, [onSearch])

	// biome-ignore lint/correctness/useExhaustiveDependencies: updateDropdownPosition reads refs and is intentionally non-stable
	useEffect(() => {
		const handler = () => {
			updatedRef.current = false
			updateDropdownPosition()
		}
		if (isOpen) {
			updateDropdownPosition()
			window.addEventListener('scroll', handler)
			window.addEventListener('resize', handler)
		}
		return () => {
			window.removeEventListener('scroll', handler)
			window.removeEventListener('resize', handler)
		}
	}, [isOpen])

	useEffect(() => {
		const dropdown = dropdownRef.current
		if (!dropdown || !isOpen || !onMenuScrollToBottom) return

		const handleScroll = () => {
			const { scrollTop, scrollHeight, clientHeight } = dropdown
			const isNearBottom = scrollHeight - scrollTop - clientHeight < 50

			if (isNearBottom && hasMore && !isLoadingMore) {
				onMenuScrollToBottom()
			}
		}

		dropdown.addEventListener('scroll', handleScroll)
		return () => {
			dropdown.removeEventListener('scroll', handleScroll)
		}
	}, [isOpen, onMenuScrollToBottom, hasMore, isLoadingMore])

	// When onSearch is provided, the parent handles filtering server-side; skip client-side filter.
	// When search is empty, prepend any selected items that aren't in the current options page
	// so they remain visible and removable without needing to re-search for them.
	const filteredOptions = (() => {
		if (searchable && !onSearch) {
			return options.filter((opt) => opt.label.toLowerCase().includes(searchQuery.toLowerCase()))
		}
		if (onSearch && searchQuery === '') {
			const optionValues = new Set(options.map((o) => o.value))
			const selectedValues = isMulti ? multiValue : selectedValue ? [selectedValue as string] : []
			const pinnedSelected = selectedValues
				.filter((v) => !optionValues.has(v) && selectedLabelsCache.current.has(v))
				.map((v) => ({ value: v, label: selectedLabelsCache.current.get(v) as string }) as SelectOption)
			return [...pinnedSelected, ...options]
		}
		return options
	})()

	// Sync controlled value into internal multiValue so programmatic updates
	// (e.g. clearAll resetting cycleIds) are reflected in visible chips.
	useEffect(() => {
		if (!isMulti) return
		setMultiValue((prev) => {
			const nextValue = Array.isArray(value) ? value : []
			const isSame = prev.length === nextValue.length && prev.every((v, i) => v === nextValue[i])
			return isSame ? prev : nextValue
		})
	}, [value, isMulti])

	// Reset focused index when search query changes
	// biome-ignore lint/correctness/useExhaustiveDependencies: intentionally only on searchQuery change
	useEffect(() => {
		setFocusedIndex(-1)
	}, [searchQuery])

	// Scroll focused option into view
	useEffect(() => {
		if (focusedIndex < 0 || !dropdownRef.current) return
		const optionEls = dropdownRef.current.querySelectorAll('[role="option"]')
		const el = optionEls[focusedIndex] as HTMLElement
		el?.scrollIntoView?.({ block: 'nearest' })
	}, [focusedIndex])

	const handleSelectOption = (option: SelectOption) => {
		if (disabled) return

		selectedLabelsCache.current.set(option.value, option.label)

		if (isMulti) {
			const newValues = multiValue.includes(option.value)
				? multiValue.filter((v) => v !== option.value)
				: [...multiValue, option.value]
			setMultiValue(newValues)
			onChange?.(newValues)
			if (searchable) {
				if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
				setSearchQuery('')
				onSearch?.('')
			}
		} else {
			setInternalValue(option.value)
			onChange?.(option.value)
			setIsOpen(false)
		}
	}

	const toggleDropdown = () => {
		if (disabled) return
		if (isOpen) {
			if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
			setSearchQuery('')
			onSearch?.('')
		}
		setIsOpen(!isOpen)
	}

	const selectedOptions = isMulti
		? multiValue
				.map(
					(v) =>
						options.find((o) => o.value === v) ??
						(selectedLabelsCache.current.has(v)
							? ({ value: v, label: selectedLabelsCache.current.get(v) as string } as SelectOption)
							: null),
				)
				.filter((o): o is SelectOption => o !== null)
		: [options.find((option) => option.value === selectedValue)].filter(Boolean)

	const displayValue = selectedOptions.length > 0 ? selectedOptions.map((opt) => opt?.label).join(', ') : placeholder

	const handleRemoveOption = (optionValue: string, e: React.MouseEvent) => {
		e.stopPropagation()
		if (disabled) return
		const newValues = multiValue.filter((v) => v !== optionValue)
		setMultiValue(newValues)
		onChange?.(newValues)
	}

	const enhancedStyleProps = {
		...styleProps,
		isOpen,
		isFocused: isOpen,
	}

	return (
		<FormElementWrapper
			label={label}
			hint={hint}
			showHintIcon={showHintIcon}
			variant={variant}
			disabled={disabled}
			isRequired={isRequired}
			styleProps={styleProps}>
			<div className='w-full relative' ref={selectRef}>
				<div
					role='combobox'
					tabIndex={disabled ? -1 : 0}
					aria-expanded={isOpen}
					aria-disabled={disabled || undefined}
					className={cn(
						formStyles.element(enhancedStyleProps),
						'flex items-center justify-between cursor-pointer min-h-[44px] outline-1 outline-transparent px-[0.5rem] py-[0.75rem]',
						// Only add default border if not in error state
						enhancedStyleProps.variant !== 'error' && 'border border-neutral-300',
						sizing === 'small' && 'p-1 m-0 text-sm min-h-[0px]',
						className,
					)}
					style={{
						width: containerWidth || '100%',
						boxSizing: 'border-box',
						margin: 0,
					}}
					onClick={toggleDropdown}
					onKeyDown={(e) => handleComboboxKeyDown(e, toggleDropdown)}>
					<div className='flex items-center flex-wrap flex-1 mr-2 min-w-0 gap-1'>
						{SelectIcon && <SelectIcon size={20} className='me-1' />}
						{displaySelectedValue && isMulti && selectedOptions.length > 0
							? selectedOptions.map((opt) => (
									<span
										key={opt?.value}
										className='flex items-center gap-1 bg-neutral-100 text-neutral-800 text-sm rounded px-2 py-0.5'>
										{opt?.label}
										<button
											type='button'
											className='text-neutral-500 hover:text-neutral-800 leading-none'
											onClick={(e) => handleRemoveOption(opt?.value ?? '', e)}
											tabIndex={-1}>
											×
										</button>
									</span>
								))
							: displaySelectedValue && <span className='truncate'>{displayValue}</span>}
					</div>
					<ChevDownIcon
						size={16}
						className={cn(
							formStyles.icon({
								variant: enhancedStyleProps.variant,
								disabled: enhancedStyleProps.disabled,
								isOpen,
							}),
							'flex-shrink-0',
						)}
					/>
				</div>

				{isOpen &&
					createPortal(
						<div
							ref={dropdownRef}
							data-select-dropdown
							className={cn(selectStyles.dropdownMenu({ isOpen, sizing }))}
							style={{
								position: 'fixed',
								top: openUpward ? 'auto' : `${dropdownPosition.top}px`,
								bottom: openUpward ? `${window.innerHeight - dropdownPosition.top}px` : 'auto',
								left: `${dropdownPosition.left}px`,
								width: `${dropdownPosition.width}px`,
								minWidth: 150,
								margin: 0,
								transform: openUpward ? 'translateY(-1px)' : 'translateY(1px)',
							}}>
							{searchable && (
								<div className='px-2 py-1 border-b border-neutral-200'>
									<input
										// biome-ignore lint/a11y/noAutofocus: intentional UX for searchable select
										autoFocus
										type='text'
										value={searchQuery}
										onChange={(e) => {
											setSearchQuery(e.target.value)
											if (onSearch) {
												if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
												searchDebounceRef.current = setTimeout(() => onSearch(e.target.value), 300)
											}
										}}
										placeholder='Search...'
										className='w-full text-sm outline-none px-2 py-1'
										onClick={(e) => e.stopPropagation()}
										onKeyDown={(e) => {
											if (e.key === 'ArrowDown') {
												e.preventDefault()
												setFocusedIndex((i) => Math.min(i + 1, filteredOptions.length - 1))
											} else if (e.key === 'ArrowUp') {
												e.preventDefault()
												setFocusedIndex((i) => Math.max(i - 1, 0))
											} else if (e.key === 'Enter' && focusedIndex >= 0) {
												e.preventDefault()
												const option = filteredOptions[focusedIndex]
												if (option) handleSelectOption(option)
											} else if (e.key === 'Escape') {
												if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
												setIsOpen(false)
												setSearchQuery('')
												onSearch?.('')
											}
										}}
									/>
								</div>
							)}
							{filteredOptions.map((option, index) => (
								<div
									role='option'
									tabIndex={0}
									aria-selected={isMulti ? multiValue.includes(option.value) : option.value === selectedValue}
									key={`${option.value}-${index}`}
									className={cn(
										selectStyles.option({
											hasIcon: Boolean(option.icon),
											isSelected: isMulti ? multiValue.includes(option.value) : option.value === selectedValue,
										}),
										index === focusedIndex && 'bg-neutral-100',
									)}
									onClick={() => handleSelectOption(option)}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') handleSelectOption(option)
									}}>
									{option.icon && <option.icon size={14} className='me-1' />} {option.label}
								</div>
							))}
							{isLoadingMore && <div className='px-4 py-2 text-center text-neutral-500 text-sm'>Loading more...</div>}
						</div>,
						document.body,
					)}
			</div>
		</FormElementWrapper>
	)
}
